// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LinkedSystem, LinkedSystemAccLinkingValue } from '../misc/LinkedSystem';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { TerceraWindowBase } from '../screen/TerceraWindowBase';
import { ControlsTypes, PanelNames, PanelsWithAccountLookup, ScreensNames } from '../UtilsClasses/FactoryConstants';
import { Resources } from '@shared/localizations/Resources';
import { DataCache } from '@shared/commons/DataCache';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { WorkSpaceManager } from '../WorkSpace/WorkSpaceManager';
import ApplicationPanelNewTemplate from '@front/templates/ApplicationPanelNewTemplate.html';
import { Account } from '@shared/commons/cache/Account';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderCancelActionHotkeysEnum } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { DynProperty } from '@shared/commons/DynProperty';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { OldThemeStyleGroup, OldThemeStyleRestorer } from '../misc/OldThemeStyleRestorer';
export class ApplicationPanel extends TerceraWindowBase {
    // #endregion
    constructor() {
        super();
        // #endregion
        // #region Instance properties
        this._defaultCurrentThemeStyleProperties = [];
        this._styleProperties = new Map();
        this.setTitle = null;
        this.Name = 'app';
        this.AllowSettings = true;
        this.headerLocaleKey = '';
        this.skipOnSave = false;
        this.skipSomeProperties = false;
        this.completed = false;
        this.dockSpawnElementsToRemove = []; // remove elements of dock system when panel destroing. fix events leak
    }
    oninit() {
        super.oninit();
        this.observe('symbolLinkValue', () => {
            this.symbolLink_Out(true);
        });
        // this.observe('accountLinkValue', () => {
        //     this.accountLink_Out(true);
        // });
        this.observe('isSymbolLinkShow isAccountLinkShow', this.LinkSystemHandler.bind(this), { init: false });
        this.observe('header', (newVal) => {
            if (newVal && this.setTitle) {
                this.setTitle(newVal);
            }
        });
        if (Resources.isHidden('allowLinkSystem')) {
            void this.set({
                isAccountLinkShow: false,
                isSymbolLinkShow: false
            });
        }
    }
    getType() {
        return null;
    }
    ContentContainerHeight() {
        let contentContainerH = this.get('height');
        const nonDockablePanelWithHeader = !this.get('dockablePanel') && this.get('showHeader');
        if (nonDockablePanelWithHeader) {
            contentContainerH -= 20;
        } // header height;
        if (this.isShowExploreTheAppLink()) {
            contentContainerH -= 25;
        }
        return contentContainerH;
    }
    getClientPanel(customPlace = false) {
        if (!this.rendered) {
            return null;
        }
        if (!customPlace) {
            return super.getClientPanel();
        }
        else {
            if (!isNullOrUndefined(this.Controls.windowPanelBodyQT)) {
                return this.Controls.windowPanelBodyQT.getClientPanel();
            }
            else {
                return super.getClientPanel();
            }
        }
    }
    hasAccountLookup() {
        return PanelsWithAccountLookup.includes(this.getType());
    }
    LinkSystemHandler(newVal, oldVal, key) {
        this.updateLinkVisible();
        if (!newVal && key === 'isAccountLinkShow') {
            if (!this.hasAccountLookup() && this.get('account') == null) {
                void this.set('account', DataCache.getPrimaryAccount());
            }
        }
    }
    oncomplete() {
        super.oncomplete();
        if (DataCache.getNumberOfAccounts() === 1) {
            void this.set({ isAccountLinkShow: false });
        }
        this.accLinkingActiveUpdate();
        if (this.get('isSetFocusOnComplete')) {
            this.setFocus();
        }
        this.completed = true;
    }
    accLinkingActiveUpdate() {
        LinkedSystem.accLinkingActive ? this.enableAccountLinking() : this.disableAccountLinking();
    }
    localize() {
        super.localize();
        this.localizeAccountLinkTooltip();
    }
    dispose() {
        const toDisp = this.dockSpawnElementsToRemove;
        if (toDisp) {
            for (let i = 0; i < toDisp.length; i++) {
                toDisp[i].dispose();
            }
        }
        if (WorkSpaceManager.currentWorkspace) {
            WorkSpaceManager.currentWorkspace.removePanel(this._guid);
        }
        super.dispose();
    }
    symbolLink_Out(newSubscriber, instrument) {
        // throw new Error("Method doesn't implemented");
    }
    symbolLink_In(symbolName) {
        // throw new Error("Method doesn't implemented");
    }
    accountLink_Out(newSubscriber, account) {
        const isCompleted = this.completed;
        if (isNullOrUndefined(account)) {
            const acc = this.get('account') || this.get('accountItem');
            if (acc == null && isCompleted) {
                return;
            }
            account = acc;
        }
        if (isCompleted) {
            LinkedSystem.setAccount(LinkedSystemAccLinkingValue, account.BstrAccount);
        }
        else if (LinkedSystem.accLinkingActive) {
            this.accountLink_In(LinkedSystem.getAccount(LinkedSystemAccLinkingValue));
        }
    }
    accountLink_In(accId, fromLinkedSystem = false) {
        if (!this.get('canLinkByAccount')) {
            return false;
        }
        if (fromLinkedSystem && !this.OnAccountLinking()) {
            return false;
        }
        const acc = DataCache.Accounts[accId];
        if (!isNullOrUndefined(acc)) {
            void this.set('account', acc);
        }
        this.localizeAccountLinkTooltip();
        return true;
    }
    onKeyDown(event) {
        super.onKeyDown(event);
        const original = event.original;
        if (original.keyCode < 48 ||
            original.keyCode > 90 ||
            original.altKey ||
            original.ctrlKey ||
            original.shiftKey) {
            return true;
        }
        const activeCtrl = this.getActiveControl();
        if (activeCtrl) {
            const actType = activeCtrl.getType();
            if (ApplicationPanel.SkipFocusOnKeyDownTypes.includes(actType)) {
                return true;
            }
        }
        this._onKeyDownProcess(event);
    }
    getActiveControl() {
        let activeCtrl = this.get('activeControl');
        if (isNullOrUndefined(activeCtrl)) {
            return null;
        }
        while (activeCtrl instanceof ApplicationPanel) {
            activeCtrl = activeCtrl.get('activeControl');
        }
        return activeCtrl;
    }
    _onKeyDownProcess(event) {
        let controls = this.Controls;
        if (controls) {
            let lookup = controls.TerceraInstrumentLookup;
            if (!lookup) {
                if (controls.topPanel) {
                    controls = controls.topPanel.Controls;
                }
            }
            if (controls) {
                lookup = controls.TerceraInstrumentLookup;
            }
            if (!lookup) {
                return;
            }
            if (!lookup.get('focused')) {
                if (lookup.textBox !== null) {
                    lookup.textBox.set({ text: '', skipfocus: true });
                    lookup.setFocus();
                    lookup.onKeyDown(event);
                }
            }
        }
    }
    getAccount() {
        var _a;
        const account = (_a = this.get('account')) !== null && _a !== void 0 ? _a : this.get('accountItem');
        if (account instanceof Account) {
            return account;
        }
        return DataCache.getPrimaryAccount();
    }
    getSelectedInstrumentID() {
        return '';
    }
    openPanel(panelName) {
        const ins = this.getSelectedInstrumentID();
        MainWindowManager.Factory.addPanel(panelName, null, function (panel) {
            panel.symbolLink_In(ins);
        });
    }
    applyStyleProperties() {
        const styleProperties = this._styleProperties.get(BaseSettings.currentTheme);
        if (isValidArray(styleProperties)) {
            this.callBack(styleProperties);
        }
    }
    themeChange() { }
    applyTheme() {
        this.themeChange();
        this._defaultCurrentThemeStyleProperties = this.getProperties(false);
        this.applyStyleProperties();
    }
    applyProperties(properties) {
        this.callBack(properties);
        this.saveOverridedStyleProperties();
    }
    RefreshInstrumentDescriptions(insArray) { }
    HotkeyPressed(hotkey, event) {
        const hotkeyActionForPanel = HotkeysManager.GetActionByHotkeyForPanel(hotkey);
        if (hotkeyActionForPanel) {
            return true;
        }
        return false;
    }
    ProcessHotkeyCancelOrder(account, instrument, action, placedFrom) {
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return false;
        }
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
            return false;
        }
        switch (action) {
            case OrderCancelActionHotkeysEnum.LastOrder:
                this.cancelLastOrder(account, instrument, placedFrom);
                return true;
            case OrderCancelActionHotkeysEnum.AllOrders:
                void DataCache.FOrderExecutor.cancelOrdersByInstrumentAndAccountPromise(instrument, account, false, placedFrom);
                return true;
            case OrderCancelActionHotkeysEnum.BuyOrder:
                this.cancelOrdersClosestToLastPrice(account, instrument, placedFrom, OperationType.Buy);
                return true;
            case OrderCancelActionHotkeysEnum.SellOrder:
                this.cancelOrdersClosestToLastPrice(account, instrument, placedFrom, OperationType.Sell);
                return true;
            default:
                return false;
        }
    }
    cancelOrdersClosestToLastPrice(account, instrument, placedFrom, side) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const lastPrice = (_a = instrument.Level1) === null || _a === void 0 ? void 0 : _a.GetLastPrice(account);
            if (!Number.isFinite(lastPrice)) {
                return;
            }
            const closestOrders = DataCache.Trading.getClosestOrdersBySideAndPrice(instrument, account, side, lastPrice);
            if (closestOrders.length > 0) {
                void DataCache.FOrderExecutor.cancelOrdersPromise(closestOrders, false, placedFrom);
            }
        });
    }
    cancelLastOrder(account, instrument, placedFrom) {
        const orders = DataCache.Trading.getOrdersByInstrumentAndAccount(instrument, account);
        let lastOrder = null;
        for (const order of orders) {
            if (isNullOrUndefined(lastOrder)) {
                lastOrder = order;
                continue;
            }
            if (order.UTCDateTime > lastOrder.UTCDateTime) {
                lastOrder = order;
            }
        }
        if (!isNullOrUndefined(lastOrder)) {
            void DataCache.FOrderExecutor.cancelOrdersPromise([lastOrder], false, placedFrom);
        }
    }
    saveOverridedStyleProperties() {
        const result = [];
        const currentProperties = this.getProperties(false);
        for (let i = 0; i < currentProperties.length; i++) {
            const newProperty = currentProperties[i];
            for (let j = 0; j < this._defaultCurrentThemeStyleProperties.length; j++) {
                const defaultProperty = this._defaultCurrentThemeStyleProperties[j];
                if (newProperty.name === defaultProperty.name) {
                    const defaultSerialized = JSON.stringify(defaultProperty.serialize().value);
                    const newSerialized = JSON.stringify(newProperty.serialize().value);
                    if (defaultSerialized !== newSerialized) {
                        result.push(newProperty);
                    }
                }
            }
        }
        this._styleProperties.set(BaseSettings.currentTheme, result);
    }
    // region ISerializable
    serialize() {
        const commonProperties = this.getProperties(true);
        const serializedData = {
            name: this.getType(),
            common: DynProperty.serialize(commonProperties),
            style: {}
        };
        for (const [key, value] of this._styleProperties) {
            serializedData.style[key] = DynProperty.serialize(value);
        }
        return serializedData;
    }
    deserialize(serialized) {
        const commonProperties = DynProperty.deserialize(serialized.common);
        if (!this.completed) {
            // Important! We need set WSId immidiately for workspace manager
            super.callBack(commonProperties);
            this.isSerializedOldScheme = false;
            this.serialized = serialized;
            return;
        }
        this._styleProperties.clear();
        if (!isNullOrUndefined(serialized.style)) {
            for (const [key, value] of Object.entries(serialized.style)) {
                this._styleProperties.set(key, DynProperty.deserialize(value));
            }
        }
        this.callBack(commonProperties);
        this.applyStyleProperties();
    }
    deserializeOldScheme(serialized) {
        const properties = DynProperty.deserialize(serialized.properties);
        if (!this.completed) {
            super.callBack(properties);
            this.isSerializedOldScheme = true;
            this.serialized = serialized;
            return;
        }
        const newDefaultStyleProperties = this.getProperties(false);
        const oldThemeStyleGroup = this.getOldThemeStyleGroup(this.getType());
        const filteredProperties = OldThemeStyleRestorer.removeOldDarkThemeDefaultStyleProperties(properties, oldThemeStyleGroup, newDefaultStyleProperties);
        this.applyProperties(filteredProperties);
    }
    deserializeDelayedData() {
        const serialized = this.serialized;
        if (isNullOrUndefined(serialized)) {
            return false;
        }
        if (this.isSerializedOldScheme) {
            this.deserializeOldScheme(serialized);
        }
        else {
            this.deserialize(serialized);
        }
        delete this.serialized;
        delete this.isSerializedOldScheme;
        return true;
    }
    getOldThemeStyleGroup(panelType) {
        switch (panelType) {
            case PanelNames.ChartPanel:
                return OldThemeStyleGroup.ChartGroup;
            case PanelNames.OptionMasterPanel:
                return OldThemeStyleGroup.OptionMasterGroup;
            case PanelNames.HeatmapPanel:
                return OldThemeStyleGroup.HeatmapGroup;
            default:
                return null;
        }
    }
}
// #region Static readonly properties
ApplicationPanel.SkipFocusOnKeyDownTypes = [
    ControlsTypes.TerceraPanel,
    ControlsTypes.TerceraAccountLookup,
    ControlsTypes.TerceraNumeric,
    ControlsTypes.TerceraTextBox,
    ControlsTypes.TerceraQuantityNumeric,
    ControlsTypes.SLTPTriggerControl,
    ControlsTypes.AccountSelector,
    PanelNames.OrderEntry,
    ControlsTypes.DynPropertyControl,
    ControlsTypes.OEQuantity,
    ControlsTypes.AdvancedChartOrderEntry,
    ControlsTypes.TerceraVisualTradingWidget,
    ControlsTypes.OEComment,
    ScreensNames.RenameScreen,
    ScreensNames.AddOverlayScreen,
    ScreensNames.TerceraLookupDropDownForm,
    ScreensNames.TerceraInstrumentLookupDropDownForm
];
TerceraWindowBase.extendWith(ApplicationPanel, {
    data: function () {
        return {
            isBeta: false,
            symbolLinkValue: TerceraLinkControlConstants.ColorConstants.NONE,
            accountLinkValue: TerceraLinkControlConstants.ColorConstants.NONE,
            isAccountLinkShow: false,
            isSymbolLinkShow: false,
            resizable: false,
            showFooter: false,
            top: 0,
            left: 0,
            dockablePanel: true,
            workSpaceId: null,
            showHeader: false,
            attached: true,
            account: null,
            canLinkByAccount: true,
            canFilterByAccount: true,
            // canSelect: false,
            loading: false,
            isSetFocusOnComplete: true,
            isIgnoreLayoutProps: false
        };
    },
    partials: {
        bodyPartial: ApplicationPanelNewTemplate
    }
});
